import { useCallback, useEffect, useRef, useState } from "react";

const COMMIT_DELAY_MS = 500;
const MAX_HISTORY = 100;

/**
 * Snapshot-based undo/redo. Changes are committed after a short pause so a
 * drag or a burst of typing becomes one undo step instead of dozens.
 */
export function useEditorHistory<T>(
  snapshot: T,
  enabled: boolean,
  onRestore: (snapshot: T) => void,
) {
  const pastRef = useRef<T[]>([]);
  const futureRef = useRef<T[]>([]);
  const committedRef = useRef<T>(snapshot);
  const latestRef = useRef<T>(snapshot);
  const restoringRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;
  const [, setVersion] = useState(0);
  const bump = () => setVersion((v) => v + 1);

  latestRef.current = snapshot;

  const commitPending = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (latestRef.current === committedRef.current) return;
    pastRef.current = [...pastRef.current, committedRef.current].slice(
      -MAX_HISTORY,
    );
    futureRef.current = [];
    committedRef.current = latestRef.current;
    bump();
  }, []);

  useEffect(() => {
    if (!enabled) {
      committedRef.current = snapshot;
      return;
    }
    if (restoringRef.current) {
      restoringRef.current = false;
      committedRef.current = snapshot;
      return;
    }
    if (snapshot === committedRef.current) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(commitPending, COMMIT_DELAY_MS);
  }, [snapshot, enabled, commitPending]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const restore = (target: T) => {
    restoringRef.current = true;
    committedRef.current = target;
    onRestoreRef.current(target);
    bump();
  };

  const undo = useCallback(() => {
    commitPending();
    const previous = pastRef.current[pastRef.current.length - 1];
    if (previous === undefined) return;
    pastRef.current = pastRef.current.slice(0, -1);
    futureRef.current = [committedRef.current, ...futureRef.current];
    restore(previous);
  }, [commitPending]);

  const redo = useCallback(() => {
    commitPending();
    const next = futureRef.current[0];
    if (next === undefined) return;
    futureRef.current = futureRef.current.slice(1);
    pastRef.current = [...pastRef.current, committedRef.current];
    restore(next);
  }, [commitPending]);

  return {
    undo,
    redo,
    canUndo:
      pastRef.current.length > 0 || latestRef.current !== committedRef.current,
    canRedo: futureRef.current.length > 0,
  };
}
